import type { GetMachinesQuery } from '#src/graphql/generated';

import { useMutation, useQuery } from '@apollo/client/react';
import {
  Box,
  Heading,
  Text,
  VStack,
  Button,
  HStack,
  Input,
} from '@chakra-ui/react';
import { useState } from 'react';

import { JsonEditor } from '#src/components/json-editor';
import { toaster } from '#src/components/ui/toaster';
import { DELETE_MACHINE } from '#src/graphql/mutations/delete-machine';
import { UPSERT_MACHINE } from '#src/graphql/mutations/upsert-machine';
import { GET_MACHINES } from '#src/graphql/queries/machines';

type MachineFromQuery = GetMachinesQuery['machines'][number];

export function MachinesPage() {
  const [selectedMachine, setSelectedMachine] =
    useState<MachineFromQuery | null>(null);
  const [editName, setEditName] = useState('');
  const [editContext, setEditContext] = useState('');
  const [editInitialContext, setEditInitialContext] = useState('');
  const [editConfig, setEditConfig] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [newMachineName, setNewMachineName] = useState('');

  const { data, loading, error, refetch } = useQuery(GET_MACHINES);
  const [upsertMachine, { loading: saving }] = useMutation(UPSERT_MACHINE);
  const [deleteMachine, { loading: deleting }] = useMutation(DELETE_MACHINE);

  const machines = data?.machines || [];

  const handleMachineSelect = (machine: MachineFromQuery) => {
    setSelectedMachine(machine);
    setEditName(machine.name);
    setEditContext(machine.context);
    setEditInitialContext(machine.initialContext);
    setEditConfig(machine.config);
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!selectedMachine) return;

    try {
      await upsertMachine({
        variables: {
          input: {
            id: selectedMachine.id,
            name: editName,
            context: editContext,
            initialContext: editInitialContext,
            config: editConfig,
          },
        },
      });

      toaster.create({
        title: 'Success',
        description: 'Machine saved successfully',
      });

      // Refresh the machines list
      await refetch();

      // Update the selected machine with new data
      const updatedMachine = machines.find(
        (machine) => machine.id === selectedMachine.id,
      );
      if (updatedMachine) {
        setSelectedMachine(updatedMachine);
      }

      setIsEditing(false);
    } catch (error) {
      toaster.create({
        title: 'Error',
        description: `Error saving machine: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    }
  };

  const handleCancel = () => {
    if (selectedMachine) {
      setEditName(selectedMachine.name);
      setEditContext(selectedMachine.context);
      setEditInitialContext(selectedMachine.initialContext);
      setEditConfig(selectedMachine.config);
    }
    setIsEditing(false);
  };

  const handleCreateMachine = () => {
    if (!newMachineName.trim()) {
      toaster.create({
        title: 'Validation Error',
        description: 'Please enter a machine name',
      });
      return;
    }

    // Create a new machine object for editing
    const newMachine: MachineFromQuery = {
      id: '', // Will be generated by the backend
      userId: '',
      name: newMachineName.trim(),
      context: '',
      initialContext: '{}',
      config: '{}',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      machineInstances: [],
      __typename: 'Machine',
    };

    setSelectedMachine(newMachine);
    setEditName(newMachine.name);
    setEditContext(newMachine.context);
    setEditInitialContext(newMachine.initialContext);
    setEditConfig(newMachine.config);
    setIsEditing(true);
    setNewMachineName('');
  };

  const handleDeleteMachine = async () => {
    if (!selectedMachine) return;

    try {
      const { data } = await deleteMachine({
        variables: {
          input: {
            id: selectedMachine.id,
          },
        },
      });

      if (data?.deleteMachine?.success) {
        toaster.create({
          title: 'Success',
          description: 'Machine deleted successfully',
        });

        // Clear selection and refresh the list
        setSelectedMachine(null);
        setIsEditing(false);
        await refetch();
      } else {
        toaster.create({
          title: 'Error',
          description:
            data?.deleteMachine?.message || 'Failed to delete machine',
          type: 'error',
        });
      }
    } catch (error) {
      toaster.create({
        title: 'Error',
        description: `Error deleting machine: ${error instanceof Error ? error.message : 'Unknown error'}`,
        type: 'error',
      });
    }
  };

  if (loading) {
    return (
      <Box>
        <Heading size="lg" color="gray.700" mb={6}>
          Machines
        </Heading>
        <Text>Loading machines...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Heading size="lg" color="gray.700" mb={6}>
          Machines
        </Heading>
        <Text color="red.500">Error loading machines: {error.message}</Text>
        <Button onClick={() => refetch()} mt={4}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Heading size="lg" color="gray.700" mb={6}>
        Machines
      </Heading>

      <HStack gap={4} align="start" height="calc(100vh - 200px)">
        {/* Machines List - File Explorer Style */}
        <Box width="320px" minWidth="320px">
          <Box bg="white" shadow="lg" borderRadius="lg" p={4} height="100%">
            <VStack gap={3} align="stretch" height="100%">
              {/* Header with Create Button */}
              <HStack justify="space-between" align="center">
                <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                  Machines
                </Text>
                <HStack gap={2}>
                  <Button onClick={() => refetch()} size="xs" variant="ghost">
                    ↻
                  </Button>
                  <Button
                    onClick={handleCreateMachine}
                    size="xs"
                    colorScheme="blue"
                  >
                    + New
                  </Button>
                </HStack>
              </HStack>

              {/* Create Machine Input */}
              <HStack gap={2}>
                <Input
                  placeholder="Machine name..."
                  value={newMachineName}
                  onChange={(e) => setNewMachineName(e.target.value)}
                  size="sm"
                  fontSize="sm"
                />
                <Button
                  onClick={handleCreateMachine}
                  size="sm"
                  colorScheme="blue"
                  disabled={!newMachineName.trim()}
                >
                  Create
                </Button>
              </HStack>

              {/* Machines List */}
              <Box flex={1} overflowY="auto">
                {machines.length === 0 ? (
                  <Text
                    color="gray.500"
                    textAlign="center"
                    py={8}
                    fontSize="sm"
                  >
                    No machines yet
                  </Text>
                ) : (
                  <VStack gap={1} align="stretch">
                    {machines.map((machine) => (
                      <Box
                        key={machine.id}
                        p={2}
                        borderRadius="md"
                        cursor="pointer"
                        bg={
                          selectedMachine?.id === machine.id
                            ? 'blue.50'
                            : 'transparent'
                        }
                        border="1px solid"
                        borderColor={
                          selectedMachine?.id === machine.id
                            ? 'blue.200'
                            : 'transparent'
                        }
                        _hover={{
                          bg: 'gray.50',
                          borderColor: 'gray.200',
                        }}
                        onClick={() => handleMachineSelect(machine)}
                      >
                        <VStack gap={1} align="stretch">
                          <Text fontSize="sm" fontWeight="medium">
                            {machine.name}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {machine.machineInstances?.length || 0} instances
                          </Text>
                          <Text fontSize="xs" color="gray.400">
                            {new Date(machine.updatedAt).toLocaleDateString()}
                          </Text>
                        </VStack>
                      </Box>
                    ))}
                  </VStack>
                )}
              </Box>
            </VStack>
          </Box>
        </Box>

        {/* Machine Editor */}
        <Box flex={1}>
          <Box bg="white" shadow="lg" borderRadius="lg" p={6} height="100%">
            {selectedMachine ? (
              <VStack gap={4} align="stretch" height="100%">
                {/* Header */}
                <HStack justify="space-between" align="center">
                  <Heading size="md" color="gray.700">
                    {isEditing ? 'Edit Machine' : 'Machine Details'}
                  </Heading>
                  <HStack gap={2}>
                    {selectedMachine && !isEditing && (
                      <Button
                        onClick={handleDeleteMachine}
                        size="sm"
                        colorScheme="red"
                        variant="outline"
                        loading={deleting}
                        loadingText="Deleting..."
                      >
                        Delete
                      </Button>
                    )}
                    {isEditing && (
                      <>
                        <Button
                          onClick={handleCancel}
                          size="sm"
                          variant="outline"
                          disabled={saving}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleSave}
                          size="sm"
                          colorScheme="blue"
                          loading={saving}
                          loadingText="Saving..."
                        >
                          Save
                        </Button>
                      </>
                    )}
                  </HStack>
                </HStack>

                {/* Machine Form */}
                <Box flex={1} overflowY="auto">
                  <VStack gap={4} align="stretch">
                    {/* Basic Info */}
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={2}>
                        Name
                      </Text>
                      {isEditing ? (
                        <Input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          placeholder="Machine name"
                          size="sm"
                        />
                      ) : (
                        <Text fontSize="sm" color="gray.700">
                          {selectedMachine.name}
                        </Text>
                      )}
                    </Box>

                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={2}>
                        Context
                      </Text>
                      {isEditing ? (
                        <Input
                          value={editContext}
                          onChange={(e) => setEditContext(e.target.value)}
                          placeholder="Machine context"
                          size="sm"
                        />
                      ) : (
                        <Text fontSize="sm" color="gray.700">
                          {selectedMachine.context}
                        </Text>
                      )}
                    </Box>

                    {/* JSON Editors */}
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={2}>
                        Initial Context (JSON)
                      </Text>
                      {isEditing ? (
                        <JsonEditor
                          value={editInitialContext}
                          onChange={setEditInitialContext}
                          height="200px"
                        />
                      ) : (
                        <Box
                          bg="gray.50"
                          border="1px solid"
                          borderColor="gray.200"
                          borderRadius="md"
                          p={3}
                          maxHeight="200px"
                          overflowY="auto"
                        >
                          <pre style={{ fontSize: '12px', margin: 0 }}>
                            {JSON.stringify(
                              JSON.parse(selectedMachine.initialContext),
                              null,
                              2,
                            )}
                          </pre>
                        </Box>
                      )}
                    </Box>

                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={2}>
                        Config (JSON)
                      </Text>
                      {isEditing ? (
                        <JsonEditor
                          value={editConfig}
                          onChange={setEditConfig}
                          height="200px"
                        />
                      ) : (
                        <Box
                          bg="gray.50"
                          border="1px solid"
                          borderColor="gray.200"
                          borderRadius="md"
                          p={3}
                          maxHeight="200px"
                          overflowY="auto"
                        >
                          <pre style={{ fontSize: '12px', margin: 0 }}>
                            {JSON.stringify(
                              JSON.parse(selectedMachine.config),
                              null,
                              2,
                            )}
                          </pre>
                        </Box>
                      )}
                    </Box>

                    {/* Machine Instances */}
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={2}>
                        Instances (
                        {selectedMachine.machineInstances?.length || 0})
                      </Text>
                      {selectedMachine.machineInstances?.length === 0 ? (
                        <Text fontSize="sm" color="gray.500">
                          No instances yet
                        </Text>
                      ) : (
                        <VStack gap={2} align="stretch">
                          {selectedMachine.machineInstances?.map((instance) => (
                            <Box
                              key={instance.id}
                              bg="gray.50"
                              border="1px solid"
                              borderColor="gray.200"
                              borderRadius="md"
                              p={3}
                            >
                              <VStack gap={1} align="stretch">
                                <HStack justify="space-between" align="center">
                                  <Text fontSize="sm" fontWeight="medium">
                                    {instance.state}
                                  </Text>
                                  <Box
                                    w={2}
                                    h={2}
                                    borderRadius="full"
                                    bg={
                                      instance.isComplete
                                        ? 'green.500'
                                        : 'yellow.500'
                                    }
                                  />
                                </HStack>
                                <Text fontSize="xs" color="gray.500">
                                  Created:{' '}
                                  {new Date(
                                    instance.createdAt,
                                  ).toLocaleString()}
                                </Text>
                              </VStack>
                            </Box>
                          ))}
                        </VStack>
                      )}
                    </Box>
                  </VStack>
                </Box>
              </VStack>
            ) : (
              <VStack gap={4} align="center" justify="center" height="100%">
                <Text color="gray.500" textAlign="center">
                  Select a machine to view and edit its details
                </Text>
              </VStack>
            )}
          </Box>
        </Box>
      </HStack>
    </Box>
  );
}
